import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import * as Analytics from 'analytics/Analytics';
import ArchiveRecordLabel from 'archive/components/ArchiveRecordLabel';
import getArchiveAwareDisplayName from 'archive/getArchiveAwareDisplayName';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {ActionMethod, ActionOrigin} from 'constants/AnalyticsConstants';
import {LegacyMeasurement} from 'constants/MeasurementConstants';
import {Container, GridType, ParentStorableData, StorableSummary} from 'constants/SampleConstants';
import {
  EDITING_SAMPLE_OWNERS_DISABLED_MESSAGE,
  SampleRestrictionStatus,
} from 'constants/SampleStatusConstants';
import * as context from 'Context';
import {TagUpdateOptions} from 'file-browser/FinderActions';
import {Tag} from 'folder-items/FileConstants';
import {writeFragmentsToCache} from 'graphql-client/ApolloWrappers';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import FragmentWrapper from 'graphql-client/FragmentWrapper';
import MissingRequiredFieldsBanner from 'inventory/required-fields/MissingRequiredFieldsBanner';
import {
  getMissingRequiredFields,
  getMissingRequiredFieldWarningsByFieldId,
  hasMissingRequiredFields,
} from 'inventory/required-fields/missingRequiredFieldsUtil';
import {EditableSampleRestrictionStatusField} from 'inventory/sample-status/EditableSampleRestrictionStatusField';
import {
  getEditRestrictedSampleUsersDisabledMessage,
  getRemoveSampleRestrictionOrSampleOwnersWarningText,
  getRestrictedSampleUsersDisplayText,
  getRestrictedSampleUsersHeaderText,
} from 'inventory/sample-status/RestrictedSampleUsersTextUtil';
import {SampleControlsFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevel';
import {hasMinimumSCRFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevelUtil';
import SampleStatusLabel from 'inventory/sample-status/SampleStatusLabel';
import {
  canCurrentUserEditRestrictedSampleUsers,
  canCurrentUserEditRestrictionStatus,
  canCurrentUserEditSampleOwners,
  getBlockingStatusWarningTooltip,
  getEditSampleRestrictionStatusDisabledMessage,
  getRestrictionAccessHeaderText,
  hasRestrictedSampleUsers,
  hasSampleOwners,
  isItemRestricted,
} from 'inventory/sample-status/SampleStatusPresentationUtils';
import {
  CheckSampleStatusParams,
  SampleStatusActionType,
} from 'inventory/sample-status/SampleStatusWarningTracking';
import {optimisticallyUpdate} from 'lib/OptimisticallyUpdate';
import {SectionWrapper} from 'metadata/MetadataEditorInfoSection';
import MetadataTagTable from 'metadata/MetadataTagTable';
import {UpdateContainerParams} from 'samples/ContainerDAO';
import {ContainerViewQuery} from 'samples/containers/__generated/ContainerViewQuery';
import {ExpirationInfoFragment} from 'samples/containers/__generated/ExpirationInfoFragment';
import {FreezeThawInfoFragment} from 'samples/containers/__generated/FreezeThawInfoFragment';
import ContainerExpirationDate, {ExpirationInfo} from 'samples/containers/ContainerExpirationDate';
import {EXPIRATION_INFO_FRAGMENT, FREEZE_THAW_INFO_FRAGMENT} from 'samples/containers/ContainerViewContainer';
import getQuantityDisplayString from 'samples/getQuantityDisplayString';
import * as TagUtil from 'schemas/tags/TagUtil';
import EditableStudiesList from 'studies/studyable/EditableStudiesList';
import {isItemStudyable} from 'studies/studyable/StudyableUtils';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {MinimalEntity} from 'ui-library/entity-input/EntityInputConstants';
import Popover from 'ui-library/floating-ui/Popover';
import * as Headers from 'ui-library/Headers';
import EditableNumericInputField from 'ui-library/inputs/EditableNumericInputField';
import EditablePartyField from 'ui-library/inputs/EditablePartyField';
import EditableTextField from 'ui-library/inputs/EditableTextField';
import {TagsInputSize} from 'ui-library/tags-input/TagsInputConstants';
import APIUtil from 'util/APIUtil';
import {readableTime} from 'util/DateUtil';
import * as Modals from 'util/Modals';
import {ElementMouseEvent} from 'util/Types';

import BarcodeValue, {BarcodeValueProps} from './BarcodeValue';
import ConfirmableMeasurementField from './ConfirmableMeasurementField';
import ContainerToolbar from './ContainerToolbar';
import GridPosition from './grids/GridPosition';
import {MeasurementType} from './MeasurementForm';
import SampleCreatorInfo from './SampleCreatorInfo';
import * as SampleUtil from './SampleUtil';
import {isGridSummary} from './SampleUtil';
import * as StorableActions from './StorableActions';
import ParentStorableDetails from './storables/ParentStorableDetails';
import {StorableSourceSelectorWithTitle} from './storables/StorableSourceSelector';

const ACTION_ORIGIN = ActionOrigin.INVENTORY_CONTAINER_PAGE;

export type FreezeThawInfo = ExtractByTypename<
  ContainerViewQuery['container'],
  'ContainerNode'
>['freezeThawInfo'];

export interface ContainerDetailsPaneProps {
  container: Container;
  isWritable: boolean;
  isUnarchivable?: boolean;
  onRequestTransfer?: (e: ElementMouseEvent) => void;
  parentStorable?: StorableSummary | null;
  // TODO(BNCH-93708): Add this onto the container type directly once no longer using REST types
  expirationInfo: ExpirationInfo | null;
  freezeThawInfo: FreezeThawInfo | null;
  showFullView: boolean;
  checkSampleStatus: (params: CheckSampleStatusParams) => Promise<boolean>;
  reload?: () => void;
  /* When provided, this override supersedes writability of all fields regardless of what specific permissions
   * they care about. Leveraged to render a view only page in the transfer modal. */
  isReadOnlyOverride?: boolean;
}

export default class ContainerDetailsPane extends React.Component<ContainerDetailsPaneProps> {
  static defaultProps = {
    isWritable: false,
    showFullView: true,
  };

  maxAvatarsForSampleRoles = 3;

  private getSampleOwnersPopoverPrefix = (): string | null => {
    const sampleOwners = this.props.container.sampleRoles?.sampleOwners ?? [];
    const hasOverflowSampleOwners = sampleOwners.length > this.maxAvatarsForSampleRoles;
    if (canCurrentUserEditSampleOwners(this.props.container) || !hasOverflowSampleOwners) {
      return null;
    }
    return ' Sample owners: ';
  };

  private getRestrictedSampleUsersPopoverPrefix = (): string | null => {
    const restrictedSampleUsers = this.props.container.sampleRoles?.restrictedSampleUsers ?? [];
    const hasOverflowRestrictedSampleUsers = restrictedSampleUsers.length > this.maxAvatarsForSampleRoles;
    if (
      canCurrentUserEditRestrictedSampleUsers(this.props.container, false) ||
      !hasOverflowRestrictedSampleUsers
    ) {
      return null;
    }

    return ` ${getRestrictedSampleUsersHeaderText()}: `;
  };

  private updateContainerWithDataModsWithAnalyticsContext = (
    containerId: string,
    containerFields: UpdateContainerParams,
    actionMethod: ActionMethod | null
  ): void => {
    context.using(
      {
        analyticsMetadata: {
          actionOrigin: ACTION_ORIGIN,
          actionMethod,
        },
      },
      null,
      () => {
        StorableActions.updateContainerWithDataMods(containerId, containerFields);
      }
    );
  };

  // Async update call, necessary for updates that use optimistic updates
  private asyncUpdateContainerWithDataModsWithAnalyticsContext = async (
    containerId: string,
    containerFields: UpdateContainerParams,
    actionMethod: ActionMethod | null,
    onCancel?: () => void,
    onFinish?: () => void
  ): Promise<void> => {
    await context.usingAsync(
      {
        analyticsMetadata: {
          actionOrigin: ACTION_ORIGIN,
          actionMethod,
        },
      },
      null,
      () => {
        StorableActions.updateContainerWithDataMods(containerId, containerFields, onFinish, onCancel);
      }
    );
  };

  private handleUpdateName = (name: string): void => {
    this.updateContainerWithDataModsWithAnalyticsContext(
      this.props.container.id,
      {name},
      ActionMethod.IN_LINE_EDIT
    );
  };

  private handleUpdateBarcode = (barcode: string): void => {
    this.updateContainerWithDataModsWithAnalyticsContext(
      this.props.container.id,
      {barcode},
      ActionMethod.IN_LINE_EDIT
    );
  };

  private handleSetParentStorable = (parentStorableData: ParentStorableData): void => {
    this.updateContainerWithDataModsWithAnalyticsContext(
      this.props.container.id,
      {parentStorableData},
      ActionMethod.IN_LINE_EDIT
    );
  };

  private handleProjectChange = (sourceId: string): void => {
    this.updateContainerWithDataModsWithAnalyticsContext(this.props.container.id, {sourceId}, null);
  };

  private handleUpdateTags = (
    tagUpdates: TagUpdateOptions
  ): Promise<Array<{id: string; tags: Array<Tag>}> | null> => {
    const {toSet} = tagUpdates;
    // Refetch so values computed from the fields, like missing required fields, reflect the save.
    StorableActions.updateContainerTagsWithDataMods(this.props.container.id, toSet, this.props.reload);
    return Promise.resolve([
      {
        id: this.props.container.id,
        tags: TagUtil.getTagTableFriendlyTags(this.props.container.tags),
      },
    ]);
  };

  private handleUpdateQuantity = (quantity?: LegacyMeasurement): void => {
    this.updateContainerWithDataModsWithAnalyticsContext(
      this.props.container.id,
      {quantity},
      ActionMethod.IN_LINE_EDIT
    );
  };

  private async updateContainerWithOptimisticUpdate<T>({
    containerFields,
    actionMethod,
    optimisticData,
    revertData,
    fragmentWrapper,
  }: {
    containerFields: UpdateContainerParams;
    actionMethod: ActionMethod | null;
    optimisticData: T;
    revertData: T;
    fragmentWrapper: FragmentWrapper<T>;
  }): Promise<void> {
    await optimisticallyUpdate({
      // onCancel reverts the optimistic update & is passed through for when the update is cancelled, either
      // through canceling data mods or the update erroring
      persistData: async (onCancel) => {
        await this.asyncUpdateContainerWithDataModsWithAnalyticsContext(
          this.props.container.id,
          containerFields,
          actionMethod,
          onCancel,
          this.props.reload
        );
      },
      writeDataToCache: (data) => {
        // We write specific fragments to cache rather than the entire container view query to better handle
        // the mixed rest/graphql state & more precisely replace only the data that is changing
        writeFragmentsToCache(fragmentWrapper, [data]);
      },
      optimisticData,
      revertData,
    });
  }

  private handleUpdateExpirationDateOverride = async (expirationDatetime: string | null): Promise<void> => {
    assert(this.props.expirationInfo != null, 'Expected expiration info');

    const isOverridden = expirationDatetime != null;
    const newExpirationDate = isOverridden
      ? expirationDatetime
      : this.props.expirationInfo.inheritedExpirationDate;

    await this.updateContainerWithOptimisticUpdate<ExpirationInfoFragment>({
      containerFields: {expirationDateOverride: expirationDatetime},
      actionMethod: ActionMethod.IN_LINE_EDIT,
      optimisticData: {...this.props.expirationInfo, expirationDate: newExpirationDate, isOverridden},
      revertData: {...this.props.expirationInfo},
      fragmentWrapper: {fragment: EXPIRATION_INFO_FRAGMENT, fragmentName: 'ExpirationInfoFragment'},
    });
  };

  private handleUpdateFreezeThawCount = async (freezeThawCount: number | null): Promise<void> => {
    assert(this.props.freezeThawInfo != null, 'Expected expiration info');
    await this.updateContainerWithOptimisticUpdate<FreezeThawInfoFragment>({
      containerFields: {freezeThawCount},
      actionMethod: ActionMethod.IN_LINE_EDIT,
      optimisticData: {...this.props.freezeThawInfo, freezeThawCount},
      revertData: {...this.props.freezeThawInfo},
      fragmentWrapper: {fragment: FREEZE_THAW_INFO_FRAGMENT, fragmentName: 'FreezeThawInfoFragment'},
    });
  };

  private checkSampleStatus = (actionAttempted: SampleStatusActionType): Promise<boolean> =>
    this.props.checkSampleStatus({
      actionAttempted,
      eventGroupId: Analytics.makeAnalyticsEventId(),
    });

  private updateSampleControls = ({
    restrictionStatus,
    sampleOwnerIds,
    restrictedSampleUserIds,
  }: {
    restrictionStatus?: SampleRestrictionStatus;
    sampleOwnerIds?: Array<string>;
    restrictedSampleUserIds?: Array<string>;
  }): void => {
    const updateParams: UpdateContainerParams = {};
    if (restrictionStatus !== undefined) {
      updateParams.restrictionStatus = restrictionStatus;
    }
    if (sampleOwnerIds !== undefined) {
      updateParams.sampleRoles = {sampleOwnerIds};
    }
    if (restrictedSampleUserIds !== undefined) {
      updateParams.sampleRoles = {...updateParams.sampleRoles, restrictedSampleUserIds};
    }

    this.updateContainerWithDataModsWithAnalyticsContext(
      this.props.container.id,
      updateParams,
      ActionMethod.IN_LINE_EDIT
    );
  };

  private handleUpdateRestrictionStatus = (
    initialRestrictionStatus: SampleRestrictionStatus,
    restrictionStatus: SampleRestrictionStatus
  ): void => {
    if (initialRestrictionStatus === restrictionStatus) {
      return;
    }

    if (restrictionStatus === SampleRestrictionStatus.UNRESTRICTED) {
      Modals.confirm(
        'Unrestrict sample?',
        `${getRemoveSampleRestrictionOrSampleOwnersWarningText()}`,
        ButtonStyle.NEGATIVE,
        {
          acceptText: 'Unrestrict',
          enableRedesign: true,
        }
      ).done(() => {
        this.updateSampleControls({restrictionStatus});
      });
    } else {
      this.updateSampleControls({restrictionStatus});
    }
  };

  private handleUpdateSampleOwners = (newSampleOwners: Array<MinimalEntity>): void => {
    const newSampleOwnerIds = newSampleOwners.map((newSampleOwner) => newSampleOwner.id);
    const isClearingSampleRoles = hasSampleOwners(this.props.container) && newSampleOwners.length === 0;
    if (isClearingSampleRoles && !hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V2)) {
      if (
        !canCurrentUserEditRestrictedSampleUsers(this.props.container, false) &&
        hasRestrictedSampleUsers(this.props.container)
      ) {
        Modals.alert(
          'Insufficient permission',
          'To remove all sample owners from a container, you must also be a sample owner.',
          ButtonStyle.PRIMARY,
          {enableRedesign: true}
        );
      } else {
        Modals.confirm(
          'Remove all sample owners?',
          `${getRemoveSampleRestrictionOrSampleOwnersWarningText()}`,
          ButtonStyle.NEGATIVE,
          {
            acceptText: 'Remove',
            enableRedesign: true,
          }
        ).done(() => {
          this.updateSampleControls({sampleOwnerIds: [], restrictedSampleUserIds: []});
        });
      }
    } else {
      this.updateSampleControls({sampleOwnerIds: newSampleOwnerIds});
    }
  };

  private handleUpdateRestrictedSampleUsers = (newRestrictedSampleUsers: Array<MinimalEntity>): void => {
    assert(
      isItemRestricted(this.props.container),
      `${getRestrictedSampleUsersDisplayText()} can only be set on restricted containers.`
    );
    const newRestrictedSampleUserIds = newRestrictedSampleUsers.map(
      (newRestrictedSampleUser) => newRestrictedSampleUser.id
    );
    this.updateSampleControls({restrictedSampleUserIds: newRestrictedSampleUserIds});
  };

  private getBarcodeValueProps = (): BarcodeValueProps => ({
    storable: this.props.container,
    isDisabled:
      this.props.isReadOnlyOverride ||
      !this.props.container.permissions.canEditBarcodes ||
      this.props.container.isFixedPlateWell,
    handleBarcodeChange: this.handleUpdateBarcode,
    confirmBeforeStartEditing: () => this.checkSampleStatus('update_barcode'),
    disableEditsWithTooltip:
      getBlockingStatusWarningTooltip(this.props.container) ||
      SampleUtil.getPossibleEditBarcodesDisabledError(this.props.container.permissions.canEditBarcodes) ||
      SampleUtil.getFixedPlateWellEditDisabledError(this.props.container.isFixedPlateWell, 'barcode'),
  });

  // A grid parent does not guarantee coordinates. Container payloads omit gridCell when it is
  // unknown, including while a move into a box or plate is still settling.
  private renderPosition(): JSX.Element {
    const {container, parentStorable} = this.props;
    const gridCell = container.gridCell;
    if (parentStorable != null && isGridSummary(parentStorable) && gridCell != null) {
      return (
        <GridPosition
          rowIndex={gridCell.rowIndex}
          columnIndex={gridCell.columnIndex}
          gridType={APIUtil.isBox(parentStorable.id) ? GridType.BOX : GridType.PLATE}
          gridWidth={parentStorable.storableSchema.width}
        />
      );
    }
    return <span>N/A</span>;
  }

  private renderTagTable(): JSX.Element {
    return (
      <div className="u-spacing-top">
        <MetadataTagTable
          emptyMessage="This container has no schema fields."
          fileId={this.props.container.sourceId}
          canAddOrRemoveTags={false}
          itemLinkAllowed={false}
          initialTags={this.props.container.tags}
          areTagsEditable={this.props.isWritable && !getBlockingStatusWarningTooltip(this.props.container)}
          isNameEditable={false}
          title="Container fields"
          showRequiredFieldErrors={true}
          warningsByFieldId={getMissingRequiredFieldWarningsByFieldId(
            this.props.container.tags,
            getMissingRequiredFields(this.props.container)
          )}
          updateTags={this.handleUpdateTags}
          confirmBeforeStartEditing={() => this.checkSampleStatus('update_fields')}
          disableEditWithTooltip={getBlockingStatusWarningTooltip(this.props.container)}
        />
      </div>
    );
  }

  override render(): JSX.Element {
    const isWellNameEditDisabled = this.props.container.isFixedPlateWell;
    return (
      <div {...getTestProps({component: 'ContainerDetailsPane', element: 'root'})}>
        {this.props.showFullView && (
          <div className="u-lr-split">
            <Headers.Header1>
              <EditableTextField
                isDisabled={
                  this.props.isReadOnlyOverride ||
                  !this.props.container.permissions.canEditNamesAndBarcodes ||
                  isWellNameEditDisabled
                }
                requestChange={this.handleUpdateName}
                value={this.props.container.name}
                confirmBeforeStartEditing={() => this.checkSampleStatus('rename')}
                disableEditsWithTooltip={
                  getBlockingStatusWarningTooltip(this.props.container) ||
                  SampleUtil.getPossibleEditNamesDisabledError(
                    this.props.container.permissions.canEditNamesAndBarcodes
                  ) ||
                  (isWellNameEditDisabled
                    ? SampleUtil.getFixedPlateWellEditDisabledError(
                        this.props.container.isFixedPlateWell,
                        'name'
                      )
                    : null)
                }
                dataTestAttrs={{component: 'NameField', element: 'root'}}
              />
              <ArchiveRecordLabel item={this.props.container} />
            </Headers.Header1>
            <ContainerToolbar
              container={this.props.container}
              isWritable={this.props.isWritable}
              isUnarchivable={this.props.isUnarchivable}
              onRequestTransfer={this.props.onRequestTransfer}
              parentStorable={this.props.parentStorable}
              reload={this.props.reload}
            />
          </div>
        )}

        {BenchlingConfig.INVENTORY_ENABLE_EXPIRATION_DATE &&
          this.props.expirationInfo?.isTrackingEnabled &&
          this.props.expirationInfo?.isExpired && (
            <div className={css(styles.bannerWrapper)}>
              <InlineBanner bannerType={InlineBannerType.WARNING} text="This container is expired." />
            </div>
          )}
        {hasMissingRequiredFields(this.props.container) && (
          <div className={css(styles.bannerWrapper)}>
            <MissingRequiredFieldsBanner
              missingRequiredFields={getMissingRequiredFields(this.props.container)}
            />
          </div>
        )}
        <div className="u-spacing-top">
          {!this.props.container.isFixedPlateWell ? (
            <Row bufferRowReset={true}>
              {hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V1) ? (
                <Col sm={4}>
                  <SectionWrapper
                    headerName={getRestrictionAccessHeaderText()}
                    elementName="restrictionAccess"
                  >
                    <SampleStatusLabel
                      item={this.props.container}
                      includeAvailability={false}
                      includeAuthorization={true}
                      itemSupportsSampleStatus={true}
                      includeMessage={true}
                      renderAllStatuses={true}
                    />
                  </SectionWrapper>
                </Col>
              ) : null}
              {hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V2) ? (
                <Col sm={4}>
                  <SectionWrapper headerName="Restriction status" elementName="restrictionStatus">
                    <Popover
                      renderContent={() => (
                        <p>{`${getEditSampleRestrictionStatusDisabledMessage(
                          this.props.container.restrictionStatus,
                          true
                        )}`}</p>
                      )}
                      isDisabled={
                        !this.props.isWritable || canCurrentUserEditRestrictionStatus(this.props.container)
                      }
                    >
                      <div className={css(styles.restrictionStatusPopover)}>
                        <EditableSampleRestrictionStatusField
                          isDisabled={
                            !this.props.isWritable ||
                            !canCurrentUserEditRestrictionStatus(this.props.container)
                          }
                          item={this.props.container}
                          onComplete={this.handleUpdateRestrictionStatus}
                        />
                      </div>
                    </Popover>
                  </SectionWrapper>
                </Col>
              ) : null}
              <Col sm={4}>
                <SectionWrapper headerName="Availability" elementName="availability">
                  <SampleStatusLabel
                    item={this.props.container}
                    includeAvailability={true}
                    includeAuthorization={false}
                    itemSupportsSampleStatus={true}
                    includeMessage={true}
                    renderAllStatuses={true}
                  />
                </SectionWrapper>
              </Col>
            </Row>
          ) : null}
          {hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V1) &&
            !this.props.container.isFixedPlateWell && (
              <Row bufferRowReset={true}>
                <Col sm={4}>
                  <SectionWrapper headerName="Sample owners" elementName="sampleOwners">
                    <EditablePartyField
                      value={this.props.container.sampleRoles?.sampleOwners ?? []}
                      onChange={this.handleUpdateSampleOwners}
                      isDisabled={
                        !this.props.isWritable || !canCurrentUserEditSampleOwners(this.props.container)
                      }
                      chipSize={TagsInputSize.SMALL}
                      enableNewEntityInput={true}
                      allowOrganizations={false}
                      allowBenchlingApps={false}
                      shouldAllowMultiple={true}
                      isFullWidth={false}
                      initialIsBeingEdited={false}
                      allowEmptyValue={true}
                      shouldUseCompactDisplay={true}
                      useAvatars={true}
                      maxAvatars={this.maxAvatarsForSampleRoles}
                      extraPopoverTextForPartyList={
                        this.props.isWritable && !canCurrentUserEditSampleOwners(this.props.container)
                          ? `${EDITING_SAMPLE_OWNERS_DISABLED_MESSAGE}.`
                          : null
                      }
                      popoverPrefixForPartyList={this.getSampleOwnersPopoverPrefix()}
                      dataTestAttrs={{component: 'SampleControlRoles', element: 'sampleOwners'}}
                    />
                  </SectionWrapper>
                </Col>
                {(!hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V2) ||
                  isItemRestricted(this.props.container)) && (
                  <Col sm={4}>
                    <SectionWrapper
                      headerName={getRestrictedSampleUsersHeaderText()}
                      elementName="restrictedSampleUsers"
                    >
                      {/* If there is at least one restricted sample user, EditablePartyField renders PartyList,
                      and the editing disabled message is included in PartyList's popover. If there are no
                      restricted sample users, then we need to render our own Popover for the disabled message
                      */}
                      <Popover
                        renderContent={() => <p>{`${getEditRestrictedSampleUsersDisabledMessage()}.`}</p>}
                        isDisabled={
                          !this.props.isWritable ||
                          canCurrentUserEditRestrictedSampleUsers(this.props.container, false) ||
                          hasRestrictedSampleUsers(this.props.container)
                        }
                      >
                        <EditablePartyField
                          value={this.props.container.sampleRoles?.restrictedSampleUsers ?? []}
                          onChange={this.handleUpdateRestrictedSampleUsers}
                          isDisabled={
                            !this.props.isWritable ||
                            !canCurrentUserEditRestrictedSampleUsers(this.props.container, false)
                          }
                          chipSize={TagsInputSize.SMALL}
                          enableNewEntityInput={true}
                          allowOrganizations={false}
                          allowBenchlingApps={true}
                          shouldAllowMultiple={true}
                          isFullWidth={false}
                          initialIsBeingEdited={false}
                          allowEmptyValue={true}
                          shouldUseCompactDisplay={true}
                          useAvatars={true}
                          maxAvatars={this.maxAvatarsForSampleRoles}
                          extraPopoverTextForPartyList={
                            this.props.isWritable &&
                            !canCurrentUserEditRestrictedSampleUsers(this.props.container, false)
                              ? `${getEditRestrictedSampleUsersDisabledMessage()}.`
                              : null
                          }
                          popoverPrefixForPartyList={this.getRestrictedSampleUsersPopoverPrefix()}
                          dataTestAttrs={{component: 'SampleControlRoles', element: 'restrictedSampleUsers'}}
                        />
                      </Popover>
                    </SectionWrapper>
                  </Col>
                )}
              </Row>
            )}
          <Row bufferRowReset={true}>
            <Col sm={4}>
              <SectionWrapper headerName="Barcode" elementName="barcode">
                <BarcodeValue {...this.getBarcodeValueProps()} />
              </SectionWrapper>
            </Col>
            <Col sm={4}>
              <SectionWrapper headerName="Schema" elementName="schema">
                <div>{getArchiveAwareDisplayName(this.props.container.storableSchema)}</div>
              </SectionWrapper>
            </Col>
            <Col sm={4}>
              <SectionWrapper elementName="storableSource">
                <StorableSourceSelectorWithTitle
                  isWritable={this.props.isWritable}
                  registryId={this.props.container.storableSchema.registryId}
                  onChange={this.handleProjectChange}
                  selectedSourceId={this.props.container.sourceId}
                  disableEditsWithTooltip={
                    this.props.container.isFixedPlateWell ? 'Project settings inherited from plate' : null
                  }
                />
              </SectionWrapper>
            </Col>
          </Row>
          <Row bufferRowReset={true}>
            <Col sm={4}>
              <SectionWrapper headerName="Location" elementName="location">
                <ParentStorableDetails
                  isWritable={
                    this.props.isWritable &&
                    !getBlockingStatusWarningTooltip(this.props.container) &&
                    !this.props.container.isFixedPlateWell
                  }
                  onSetParentStorable={this.handleSetParentStorable}
                  parentStorable={this.props.parentStorable}
                  confirmBeforeEditing={() => this.checkSampleStatus('move_location')}
                />
              </SectionWrapper>
            </Col>
            <Col sm={4}>
              <SectionWrapper headerName={getQuantityDisplayString()} elementName="quantity">
                <ConfirmableMeasurementField
                  isDisabled={
                    !this.props.isWritable || getBlockingStatusWarningTooltip(this.props.container) != null
                  }
                  measurement={this.props.container.quantity}
                  onComplete={this.handleUpdateQuantity}
                  measurementType={MeasurementType.QUANTITY}
                  confirmBeforeStartEditing={() => this.checkSampleStatus('update_quantity')}
                  disableEditsWithTooltip={getBlockingStatusWarningTooltip(this.props.container)}
                />
              </SectionWrapper>
            </Col>
            <Col sm={4}>
              <SectionWrapper headerName="Position" elementName="position">
                {this.renderPosition()}
              </SectionWrapper>
            </Col>
          </Row>
          <Row bufferRowReset={true}>
            <Col sm={4}>
              <SectionWrapper headerName="Creator" elementName="creator">
                <SampleCreatorInfo creator={this.props.container.creator} />
              </SectionWrapper>
            </Col>
            <Col sm={4}>
              <SectionWrapper headerName="Created" elementName="createdAt">
                <span>{readableTime(this.props.container.createdAt)}</span>
              </SectionWrapper>
            </Col>
            {BenchlingConfig.STUDIES_ENABLE && isItemStudyable(this.props.container) && (
              <Col sm={4}>
                <SectionWrapper headerName="Studies" elementName="studies">
                  <EditableStudiesList studyable={this.props.container} isWritable={this.props.isWritable} />
                </SectionWrapper>
              </Col>
            )}
          </Row>
          <Row bufferRowReset={true}>
            {BenchlingConfig.INVENTORY_ENABLE_EXPIRATION_DATE &&
              this.props.expirationInfo?.isTrackingEnabled && (
                <Col sm={4}>
                  <ContainerExpirationDate
                    expirationInfo={this.props.expirationInfo}
                    onUpdateExpirationOverride={this.handleUpdateExpirationDateOverride}
                    isDisabled={!this.props.isWritable}
                    confirmBeforeStartEditing={() => this.checkSampleStatus('update_expiration_date')}
                    disableEditsWithTooltip={getBlockingStatusWarningTooltip(this.props.container)}
                  />
                </Col>
              )}
            {BenchlingConfig.INVENTORY_ENABLE_FREEZE_THAW && this.props.freezeThawInfo?.isTrackingEnabled && (
              <Col sm={4}>
                <SectionWrapper headerName="Freeze/thaw count" elementName="freezeThawCount">
                  <EditableNumericInputField
                    invalidValueErrorText="Freeze/thaw count cannot be negative"
                    shouldValidateInteger={true}
                    value={this.props.freezeThawInfo.freezeThawCount}
                    onChange={this.handleUpdateFreezeThawCount}
                    useIntegerIncrementButtons={true}
                    min={0}
                    isDisabled={!this.props.isWritable}
                    confirmBeforeStartEditing={() => this.checkSampleStatus('update_freeze_thaw_count')}
                    disableEditsWithTooltip={getBlockingStatusWarningTooltip(this.props.container)}
                  />
                </SectionWrapper>
              </Col>
            )}
          </Row>
        </div>
        {this.renderTagTable()}
      </div>
    );
  }
}

const styles = StyleSheet.create({
  restrictionStatusPopover: {
    width: '100%',
  },
  bannerWrapper: {
    paddingTop: 20,
  },
});
